import React, { useState, useEffect } from 'react';
import { Radio, DatePicker, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styles from './AdjustmentsBlade.module.css';
import BladeSection from '../../BladeSection';
import type { RateContractInfo } from '@pages/meter-read-details/useMeterReadDetailsPage';
import { toTestAttrValue, type TestAttrs } from '@utils/testAttr';

export type RateRow = {
  key: string;
  label: string;
  effectiveFrom: Dayjs | null;
  effectiveTo: Dayjs | null;
  rate: string;
};

// Must match MAP_TO_DB in meterDetails/resolver.js
const DB_TYPE_TO_KEY: Record<string, string> = {
  Retroactive: 'RETROACTIVE',
  Interim: 'INTERIM',
};

// ── Date boundary helpers ─────────────────────────────────────────────────────
//
// TESTING NOTE:
// Currently using calendar month - 2 to match the backend testing offset.
// TODO: Change `subtract(2, 'month')` back to `subtract(1, 'month')` for production.
//       Production rule: invoicing month = calendar month - 1
//
const getInvoicingMonth = (): Dayjs =>
  dayjs().subtract(1, 'month').startOf('month');

//
// disabledDate per rate type
const disabledDateFor: Record<string, (d: Dayjs) => boolean> = {
  // INTERIM: allow from invoicing month onward (current + future invoicing months)
  INTERIM: (d) => d.isBefore(getInvoicingMonth(), 'month'),
  // RETROACTIVE: only past months allowed — current month and future months are disabled
  RETROACTIVE: (d) => d.isSame(getInvoicingMonth(), 'month') || d.isAfter(getInvoicingMonth(), 'month'),
};

function buildInitialRows(rateContracts: RateContractInfo[]): RateRow[] {
  const byKey = new Map(
    rateContracts.map(rc => [DB_TYPE_TO_KEY[rc.rateType] ?? rc.rateType, rc])
  );
  return (['RETROACTIVE', 'INTERIM'] as const).map((key) => {
    const rc = byKey.get(key);
    return {
      key,
      label: key,
      effectiveFrom: rc?.effectiveFrom ? dayjs(rc.effectiveFrom, 'YYYY-MM') : null,
      effectiveTo: rc?.effectiveTo ? dayjs(rc.effectiveTo, 'YYYY-MM') : null,
      rate: rc?.rate != null ? String(rc.rate) : '',
    };
  });
}

interface RateAdjustmentSectionProps {
  rateContracts: RateContractInfo[];
  activeRateType?: string | null;
  monthYear?: string;
  isApproved?: boolean;
  onChange: (selectedRateType: string | null, rateRows: RateRow[]) => void;
  onClear: (clearedType: string | null) => void;
}

const RateAdjustmentSection = ({
  rateContracts,
  activeRateType = null,
  monthYear,
  isApproved = false,
  onChange,
  onClear,
}: RateAdjustmentSectionProps) => {
  const [rateRows, setRateRows] = useState<RateRow[]>(() => buildInitialRows(rateContracts));

  const toKey = (dbType: string | null) => (dbType ? (DB_TYPE_TO_KEY[dbType] ?? null) : null);

  // activeRateType reflects MeterMaster.rate_id, which is not updated for RETROACTIVE.
  // Fall back to rateContracts to detect a saved non-Contract rate type.
  // For RETROACTIVE, only pre-select if the current monthYear falls within the contract's effective range.
  const deriveSelectedType = (dbType: string | null, contracts: RateContractInfo[], month?: string) => {
    const fromActive = toKey(dbType);
    if (fromActive) return fromActive;
    const nonContract = contracts.find(rc => {
      if (rc.rateType === 'Contract' || !DB_TYPE_TO_KEY[rc.rateType]) return false;
      if (rc.rateType === 'Retroactive' && month) {
        return rc.effectiveFrom != null && rc.effectiveTo != null
          && month >= rc.effectiveFrom && month <= rc.effectiveTo;
      }
      return true;
    });
    return nonContract ? DB_TYPE_TO_KEY[nonContract.rateType] : null;
  };

  const [selectedRateType, setSelectedRateType] = useState<string | null>(
    () => deriveSelectedType(activeRateType, rateContracts, monthYear)
  );

  const [openPicker, setOpenPicker] = useState<string | null>(null);

  useEffect(() => {
    setRateRows(buildInitialRows(rateContracts));
    setSelectedRateType(deriveSelectedType(activeRateType, rateContracts, monthYear));
  }, [rateContracts, activeRateType, monthYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRateRow = (key: string, field: keyof RateRow, value: any) => {
    const next = rateRows.map((r) => r.key === key ? { ...r, [field]: value } : r);
    setRateRows(next);
    onChange(selectedRateType, next);
  };

  const handleSelectType = (key: string) => {
    setSelectedRateType(key);
    onChange(key, rateRows);
  };

  const handleClear = () => {
    const previousType = selectedRateType;
    const reset = buildInitialRows(rateContracts);
    setSelectedRateType(null);
    setRateRows(reset);
    onChange(null, reset);
    onClear(previousType);
  };

  const columns = [
    {
      title: 'Rate Type',
      dataIndex: 'label',
      width: 145,
      render: (_: string, record: RateRow) => (
        <Radio
          value={record.key}
          checked={selectedRateType === record.key}
          onChange={() => handleSelectType(record.key)}
          disabled={isApproved}
          data-testid="rate-type-radio"
        >
          <span className={styles.rateTypeLabel}>{record.label}</span>
        </Radio>
      ),
    },
    {
      title: 'Effective From',
      dataIndex: 'effectiveFrom',
      render: (_: any, record: RateRow) => {
        const pickerId = `from-${record.key}`;
        const isSelected = selectedRateType === record.key;
        const isDisabled = isApproved || !isSelected;
        return (
          <div
            role="presentation"
            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            onMouseDown={(e) => {
              if (isDisabled) return;
              e.stopPropagation();
              setOpenPicker(pickerId);
            }}
          >
            <DatePicker
              picker="month"
              format="YYYY-MM"
              style={{ width: '100%' }}
              value={record.effectiveFrom}
              open={openPicker === pickerId}
              disabled={isDisabled}
              disabledDate={disabledDateFor[record.key]}
              onOpenChange={(open) => setOpenPicker(open ? pickerId : null)}
              onChange={(val) => { updateRateRow(record.key, 'effectiveFrom', val); setOpenPicker(null); }}
              getPopupContainer={() => document.body}
              styles={{ popup: { root: { zIndex: 10002 } } }}
              allowClear={false}
              data-testid="rate-effective-from"
            />
          </div>
        );
      },
    },
    {
      title: 'Effective To',
      dataIndex: 'effectiveTo',
      render: (_: any, record: RateRow) => {
        const pickerId = `to-${record.key}`;
        const isSelected = selectedRateType === record.key;
        const isDisabled = isApproved || !isSelected;
        return (
          <div
            role="presentation"
            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            onMouseDown={(e) => {
              if (isDisabled) return;
              e.stopPropagation();
              setOpenPicker(pickerId);
            }}
          >
            <DatePicker
              picker="month"
              format="YYYY-MM"
              style={{ width: '100%' }}
              value={record.effectiveTo}
              open={openPicker === pickerId}
              disabled={isDisabled}
              disabledDate={disabledDateFor[record.key]}
              onOpenChange={(open) => setOpenPicker(open ? pickerId : null)}
              onChange={(val) => { updateRateRow(record.key, 'effectiveTo', val); setOpenPicker(null); }}
              getPopupContainer={() => document.body}
              styles={{ popup: { root: { zIndex: 10002 } } }}
              allowClear={false}
              data-testid="rate-effective-to"
            />
          </div>
        );
      },
    },
    {
      title: 'Rate in CAD',
      dataIndex: 'rate',
      width: 120,
      render: (_: any, record: RateRow) => {
        const isSelected = selectedRateType === record.key;
        return (
          <div className={styles.rateInputWrapper}>
            <span className={styles.rateCurrencyPrefix}>$</span>
            <input
              type="number"
              className={styles.rateInput}
              placeholder="0.00"
              value={record.rate}
              disabled={isApproved || !isSelected}
              onChange={(e) => updateRateRow(record.key, 'rate', e.target.value)}
              data-testid="rate-input"
            />
          </div>
        );
      },
    },
  ];

  return (
    <BladeSection
      title="Rate Adjustment"
      onClear={handleClear}
      showClear={!!selectedRateType && !isApproved}
    >
      {isApproved ? (
        <p className={styles.rateAdjustmentHint} data-testid="rate-adjustment-hint" data-locked="true">
          Rate adjustments are locked — invoice has been approved.
        </p>
      ) : (
        <p className={styles.rateAdjustmentHint} data-testid="rate-adjustment-hint" data-locked="false">
          Select a rate type and set the effective date range and rate. Costs will be
          recalculated for all months in the selected range on save.
        </p>
      )}
      <Table
        dataSource={rateRows}
        columns={columns}
        pagination={false}
        rowKey="key"
        bordered
        size="small"
        className={styles.rateTable}
        data-testid="rate-adjustment-table"
        onRow={(record) => ({
          'data-testid': 'rate-row',
          'data-rate-type': toTestAttrValue(record.key),
          'data-selected': String(selectedRateType === record.key),
        }) as TestAttrs}
      />
    </BladeSection>
  );
};

export default RateAdjustmentSection;